import React, { FC, useState } from "react";

import {
  Divider,
  ToggleButton as MuiToggleButton,
  ToggleButtonProps as MuiToggleButtonProps,
  Stack,
  ToggleButtonGroup,
} from "@mui/material";

import HelpInfo from "../../../components/HelpInfo";
import { ReduxTextField } from "../../../components/Redux/TextField";
import SettingSection from "../../../components/SettingSection";
import {
  getRouteFormProperty,
  setRouteFormError,
  setRouteFormProperties,
  setRouteFormProperty,
} from "../../../slices/routes";
import { useDispatch, useSelector } from "../../../store";

type ToggleButtonProps = MuiToggleButtonProps & {
  docsKey?: string;
};
const ToggleButton: FC<ToggleButtonProps> = ({
  docsKey,
  children,
  sx,
  ...props
}) => {
  return (
    <MuiToggleButton
      {...props}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        ...sx,
      }}
    >
      <span>{children}</span>
      {docsKey && <HelpInfo docsKey={docsKey} />}
    </MuiToggleButton>
  );
};

type Mode = "preserve" | "destination" | "literal" | "header" | "regex";

type HostHeaderSettingProps = {
  readonly: boolean;
};
const HostHeaderSetting: FC<HostHeaderSettingProps> = ({ readonly }) => {
  const dispatch = useDispatch();
  const preserveHostHeader = useSelector(
    (state) => state.routes.form?.preserveHostHeader,
  );
  const hostRewrite = useSelector((state) => state.routes.form?.hostRewrite);
  const hostRewriteHeader = useSelector(
    (state) => state.routes.form?.hostRewriteHeader,
  );
  const hostPathRegexRewritePattern = useSelector(
    (state) => state.routes.form?.hostPathRegexRewritePattern,
  );
  const [mode, setMode] = useState<Mode>(() =>
    preserveHostHeader
      ? "preserve"
      : hostRewrite
        ? "literal"
        : hostRewriteHeader
          ? "header"
          : hostPathRegexRewritePattern
            ? "regex"
            : "destination",
  );

  const handleChangeMode = (
    _: React.MouseEvent<HTMLElement>,
    value: string,
  ) => {
    const mode = (value === "rewrite" ? "destination" : value) as Mode;
    dispatch(
      setRouteFormProperties([
        { path: "preserveHostHeader", value: mode === "preserve" },
        { path: "hostRewrite", value: "" },
        { path: "hostRewriteHeader", value: "" },
        { path: "hostPathRegexRewritePattern", value: "" },
        { path: "hostPathRegexRewriteSubstitution", value: "" },
      ]),
    );
    setMode(mode);
  };

  return (
    <SettingSection
      label="Host Header"
      fullWidthChildren={
        ["literal", "header", "regex"].includes(mode) && (
          <>
            {mode === "literal" && (
              <SettingSection
                label="Host Rewrite to Literal"
                docsKey="host-rewrite"
              >
                <ReduxTextField
                  disabled={readonly}
                  getter={getRouteFormProperty}
                  setter={setRouteFormProperty}
                  errorHandler={setRouteFormError}
                  path={"hostRewrite"}
                  autoFocus
                  placeholder="e.g. Host"
                />
              </SettingSection>
            )}

            {mode === "header" && (
              <SettingSection
                label="Host Rewrite to Header"
                docsKey="host-rewrite-header"
              >
                <ReduxTextField
                  disabled={readonly}
                  getter={getRouteFormProperty}
                  setter={setRouteFormProperty}
                  errorHandler={setRouteFormError}
                  path={"hostRewriteHeader"}
                  autoFocus
                  placeholder="e.g. X-My-Host"
                />
              </SettingSection>
            )}

            {mode === "regex" && (
              <>
                <SettingSection
                  label="Host Path Regex Rewrite Pattern"
                  docsKey="host-path-rewrite-header"
                >
                  <ReduxTextField
                    disabled={readonly}
                    getter={getRouteFormProperty}
                    setter={setRouteFormProperty}
                    errorHandler={setRouteFormError}
                    path={"hostPathRegexRewritePattern"}
                    autoFocus
                    placeholder="e.g. ^/(.+)/.+$"
                  />
                </SettingSection>

                <Divider />

                <SettingSection
                  label="Host Path Regex Rewrite Substitution"
                  docsKey="host-path-rewrite-substitution"
                >
                  <ReduxTextField
                    disabled={readonly}
                    getter={getRouteFormProperty}
                    setter={setRouteFormProperty}
                    errorHandler={setRouteFormError}
                    path={"hostPathRegexRewriteSubstitution"}
                    placeholder="e.g. \\1"
                  />
                </SettingSection>
              </>
            )}
          </>
        )
      }
    >
      <Stack gap={2}>
        <ToggleButtonGroup
          aria-label="Host Header Mode"
          value={mode === "preserve" ? "preserve" : "rewrite"}
          onChange={handleChangeMode}
          exclusive
          size="small"
          fullWidth
        >
          <ToggleButton value="preserve" docsKey="preserve-host-header">
            Preserve
          </ToggleButton>
          <ToggleButton value="rewrite">Rewrite</ToggleButton>
        </ToggleButtonGroup>
        {mode !== "preserve" && (
          <ToggleButtonGroup
            aria-label="Host Header Rewrite Mode"
            value={mode}
            onChange={handleChangeMode}
            exclusive
            size="small"
            fullWidth
          >
            <ToggleButton value="destination" docsKey="host-upstream">
              To Upstream
            </ToggleButton>
            <ToggleButton value="literal" docsKey="host-rewrite">
              To Literal
            </ToggleButton>
            <ToggleButton value="header" docsKey="host-rewrite-header">
              To Header
            </ToggleButton>
            <ToggleButton value="regex" docsKey="host-path-rewrite-header">
              With Regex
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </Stack>
    </SettingSection>
  );
};
export default HostHeaderSetting;
